import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { apiFetch } from '@/lib/api/client';
import { queryKeys } from '@/lib/queryKeys';
import { useSites } from '@/lib/queries';
import type { MonthlyMeetingScope } from '@/lib/types';
import { formatEventTime } from '@/lib/utils/calendar';
import { formatDate } from '@/lib/utils/week';
import { WidgetFrame, WidgetRow } from '@/pages/home/widgets/WidgetFrame';

/**
 * 오늘 잡힌 월간회의 일정. 본사·현장 회의를 함께 보여준다.
 *
 * ## 펼치는 계산은 서버가 한다
 *
 * 예전에는 규칙 목록을 받아 화면이 `expandOccurrences` 로 펼쳤다. 그런데 **그날만 취소·변경**한
 * 기록(예외)을 목에서 읽고 있어서, 서버에 실제 예외가 있는데도 **취소한 회의가 홈에는 그대로
 * 떠 있었다**(DOC-66 §6.1).
 *
 * 지금은 서버가 반복 규칙과 예외를 함께 보고 오늘 회차를 내려준다(`/v1/home/meetings/today`).
 * 홈 인사말도 **같은 계산**을 읽으므로("금일 09:00에 회의가 예정되어 있습니다"), 한 화면 안에서
 * 두 자리가 다른 말을 할 수 없다.
 *
 * `scope` 로 본사(`hq`)와 현장(`site`·`field`)을 가른다. 본사는 카테고리로, 현장은 현장명으로
 * 일정을 구분하므로 각 줄에 소속을 배지로 달고, 현장 회의는 현장명을 함께 보여준다.
 *
 * ## 두 주를 2×7 달력으로 본다 (사용자 지시 2026-10-21 · 10-22)
 *
 * 이번 주 일요일부터 다음 주 토요일까지(`/v1/home/meetings/weeks`). 금요일에 열어도 다음 주가 보인다.
 * 일요일이 맨 왼쪽이다(사용자 지시 2026-10-22).
 * 위젯 폭에서 한 칸은 50px 남짓이라 칸에는 날짜와 건수만 두고, 고른 날의 회의는 달력 아래 목록으로
 * 낸다(처음엔 오늘). 지난 날은 흐리게, 오늘은 바탕색으로 짚는다.
 */

const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토'];

const SCOPE_PATH: Record<MonthlyMeetingScope, string> = {
  hq: '/meetings/monthly/hq',
  field: '/meetings/monthly/field',
  site: '/meetings/monthly/site',
};

/** 서버가 펼친 회차 한 줄. 달력이 쓰는 회차보다 칸이 적다 — 위젯은 언제·무엇·어디만 그린다. */
type TodayOccurrence = {
  id: string;
  scope: MonthlyMeetingScope;
  title: string;
  /** `YYYY-MM-DD`. 옛 서버(재시작 전)는 이 칸 없이 오늘 것만 준다 — 없으면 오늘로 읽는다. */
  date?: string;
  eventTime: string | null;
  siteId: string | null;
};

/** 이번 주 일요일부터 14일. 서버(`HomeMeetingController#weeks`)와 같은 범위다. */
function twoWeeks(base: Date): string[] {
  const sunday = new Date(base);
  sunday.setDate(base.getDate() - base.getDay());
  return Array.from({ length: 14 }, (_, offset) => {
    const day = new Date(sunday);
    day.setDate(sunday.getDate() + offset);
    return formatDate(day);
  });
}

export function MeetingsWeekWidget() {
  // 내가 속한 회의만 온다 — 본사 인원은 본사 회의(내 부서 것/부서 지정 없는 것), 현장 인원은
  // 자기 현장 회의. 판정은 서버가 소속(auth_user_scopes)으로 한다. 화면에서 거르지 않는 이유:
  // 위젯마다 같은 판정을 다시 짜면 "내 회의" 의 뜻이 화면마다 달라진다.
  const occurrences = useQuery({
    queryKey: queryKeys.myMeetingsWeeks,
    queryFn: () => apiFetch<TodayOccurrence[]>('/v1/home/meetings/weeks'),
  });
  const sites = useSites();

  const today = formatDate(new Date());
  const [selected, setSelected] = useState(today);

  const nameBySite = new Map((sites.data ?? []).map((site) => [site.id, site.name] as const));
  const rows = occurrences.data ?? [];
  const byDate = new Map<string, TodayOccurrence[]>();
  for (const row of rows) {
    const date = row.date ?? today;
    byDate.set(date, [...(byDate.get(date) ?? []), row]);
  }

  const dates = twoWeeks(new Date());
  const todayCount = byDate.get(today)?.length ?? 0;
  const selectedItems = byDate.get(selected) ?? [];
  const selectedDay = new Date(`${selected}T00:00:00`);

  return (
    <WidgetFrame
      title="오늘 회의"
      icon="calendar"
      to="/meetings/monthly/hq"
      pending={occurrences.isPending}
      error={occurrences.error}
      headerRight={todayCount > 0 ? <span className="badge badge-gray">오늘 {todayCount}건</span> : undefined}
    >
      <div className="grid grid-cols-7 gap-1 text-center">
        {DAY_NAMES.map((head, index) => (
          <span
            key={head}
            className="text-ui-2xs"
            style={{ fontWeight: 600, color: index === 0 ? '#dc2626' : index === 6 ? '#2563eb' : 'var(--text-muted)' }}
          >
            {head}
          </span>
        ))}

        {dates.map((date) => {
          const count = byDate.get(date)?.length ?? 0;
          const isPast = date < today;
          const isToday = date === today;
          const isSelected = date === selected;

          return (
            <button
              key={date}
              type="button"
              onClick={() => setSelected(date)}
              className="flex flex-col items-center rounded-md py-1"
              style={{
                opacity: isPast ? 0.45 : 1,
                background: isToday ? 'var(--accent-bg)' : isSelected ? 'var(--bg-hover)' : undefined,
                border: `1px solid ${isToday ? 'var(--accent-light)' : isSelected ? 'var(--border)' : 'transparent'}`,
              }}
            >
              <span
                className="text-ui-xs"
                style={{
                  fontWeight: isToday ? 700 : 500,
                  color: isPast ? 'var(--text-muted)' : isToday ? 'var(--accent)' : 'var(--text-secondary)',
                }}
              >
                {Number(date.slice(8))}
              </span>
              <span
                className="text-ui-2xs"
                style={{
                  minHeight: '14px',
                  fontWeight: 700,
                  color: isPast ? 'var(--text-muted)' : 'var(--accent-light)',
                }}
              >
                {count > 0 ? `●${count}` : ''}
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-2 pt-2" style={{ borderTop: '1px solid var(--border)', opacity: selected < today ? 0.55 : 1 }}>
        <p
          className="pb-1 text-ui-2xs"
          style={{ fontWeight: 700, color: selected === today ? 'var(--accent)' : 'var(--text-muted)' }}
        >
          {selectedDay.getMonth() + 1}/{selectedDay.getDate()} ({DAY_NAMES[selectedDay.getDay()]})
          {selected === today ? ' 오늘' : ''}
        </p>

        {selectedItems.length === 0 ? (
          <p className="py-2 text-center text-ui-xs" style={{ color: 'var(--text-muted)' }}>
            예정된 회의 일정이 없습니다.
          </p>
        ) : (
          <div className="space-y-0.5">
            {selectedItems.map((occurrence) => {
              const isHq = occurrence.scope === 'hq';
              const place = occurrence.siteId ? nameBySite.get(occurrence.siteId) : null;
              const meta = [formatEventTime(occurrence.eventTime), isHq ? null : place]
                .filter(Boolean)
                .join(' · ') || undefined;

              return (
                <WidgetRow
                  key={occurrence.id}
                  to={SCOPE_PATH[occurrence.scope]}
                  leading={
                    <span
                      className="flex-shrink-0 text-center text-ui-2xs"
                      style={{
                        width: '32px',
                        fontWeight: 700,
                        padding: '2px 0',
                        borderRadius: '6px',
                        color: isHq ? '#1d4ed8' : '#b45309',
                        background: isHq ? '#eff6ff' : '#fff7ed',
                      }}
                    >
                      {isHq ? '본사' : '현장'}
                    </span>
                  }
                  title={<span className="truncate">{occurrence.title}</span>}
                  meta={meta}
                />
              );
            })}
          </div>
        )}
      </div>
    </WidgetFrame>
  );
}
